import { UserRole } from '@today/types'

/**
 * Declarative navigation config (ADR-0008).
 *
 * Filtered at render time by `filterNav(config, viewer)`.
 *
 * Gating conventions:
 *
 * 1. Every group MUST declare `visibleFor` explicitly. No implicit
 *    "open to all" groups — keeps the access intent obvious at a glance.
 * 2. Item-level `minRole` is the per-item ordinal floor
 *    (VIEWER < USER < ADMIN < SUPER_ADMIN). It composes AND-wise with the
 *    group's `visibleFor`: an item renders only when both pass.
 * 3. `visibleIf` is a closed-set string predicate resolved by `filterNav`.
 *    The template ships with no predicates registered; downstream projects
 *    extend the union below + `filterNav`'s switch when adding one.
 *
 * Template ships a minimal scaffold: a Dashboard for everyone with an
 * account, and an Admin → Settings entry for the admin roles. Downstream
 * projects extend `navigationConfig` with their own groups and items.
 */

// `visibleFor` is an EXACT membership test (filterNav), not an ordinal floor, so
// every role that should see a group must be listed. Since the coarse ladder
// gained a level per RBAC role (ORG_MANAGER < ORG_ADMIN < PLATFORM_ADMIN <
// SUPER_ADMIN), omitting one silently hides the group from it.
const ADMIN_ROLES: UserRole[] = [
	UserRole.ADMIN,
	UserRole.ORG_MANAGER,
	UserRole.ORG_ADMIN,
	UserRole.PLATFORM_ADMIN,
	UserRole.SUPER_ADMIN,
]
const USER_ROLES: UserRole[] = [UserRole.USER, ...ADMIN_ROLES]

/** Closed set of `visibleIf` predicates, resolved by `filterNav`. */
export type NavigationPredicate =
	| 'can-read-customers'
	| 'can-read-products'
	| 'can-read-offers'
	| 'can-manage-locales'
	| 'can-read-content'
	| 'can-approve-content'
	| 'can-manage-content-types'
	| 'can-open-admin'

export interface NavigationItem {
	label: string
	route: string
	minRole?: UserRole
	visibleFor?: UserRole[]
	/**
	 * Closed-set string predicate resolved at filter time. Template ships
	 * no predicates; extend this union + `filterNav`'s switch when adding one.
	 */
	visibleIf?: NavigationPredicate
}

export interface NavigationGroup {
	group: string
	/** Optional display label for the section header. Falls back to `group` when omitted. */
	label?: string
	visibleFor: UserRole[]
	items: NavigationItem[]
}

export const navigationConfig: NavigationGroup[] = [
	{
		group: 'personal',
		visibleFor: USER_ROLES,
		items: [
			{ label: 'Dashboard', route: '/dashboard', minRole: UserRole.USER },
			// TO-418: permission-gated, not role-gated — the viewer needs customer.read.
			{ label: 'Customers', route: '/customers', visibleIf: 'can-read-customers' },
			// TO-416: the product catalog screens need product.read.
			{ label: 'Products', route: '/products', visibleIf: 'can-read-products' },
			{ label: 'Product categories', route: '/product-categories', visibleIf: 'can-read-products' },
			// TO-466: the offer screens need offer.read.
			{ label: 'Offers', route: '/offers', visibleIf: 'can-read-offers' },
			// TO-420: the content screens, each gated by the content permission it needs.
			{ label: 'Texts', route: '/content/texts', visibleIf: 'can-read-content' },
			{ label: 'Approval inbox', route: '/content/inbox', visibleIf: 'can-approve-content' },
			{ label: 'Content types', route: '/content/types', visibleIf: 'can-manage-content-types' },
			{ label: 'Tenant defaults', route: '/content/tenant-defaults', visibleIf: 'can-read-content' },
		],
	},
	{
		group: 'admin',
		label: 'Admin',
		// Every admin role, not just SUPER_ADMIN. The individual items and screens
		// gate on the permissions they actually require (the read-mostly
		// PLATFORM_ADMIN, TO-626, sees only Settings until its operator screens land).
		visibleFor: ADMIN_ROLES,
		items: [
			// TO-189: the admin shell, for viewers holding a permission some admin section needs.
			{ label: 'Admin', route: '/admin', visibleIf: 'can-open-admin' },
			{ label: 'Settings', route: '/settings' },
			// TO-416: resolves the viewer's tenant slug, then opens /t/{slug}/admin/locale-settings.
			{ label: 'Locale settings', route: '/admin/locale-settings', visibleIf: 'can-manage-locales' },
		],
	},
]
